import React, { useEffect, useState } from "react";
import { collection, query, where, onSnapshot, updateDoc, doc, orderBy, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { isTaskOverdue } from "../utils/taskHelpers";
import { toLocalDateStr } from "../utils/dateUtils";
import { useAuth } from "../contexts/AuthContext";
import { dirittiPieni } from "../utils/condivisione";
import { avvisaCambioStato } from "../utils/avvisiAttivita";
import { useToast } from "../utils/Toast";
import { campiCompletamento, avvisoCompletamentoDiAltri } from "../utils/completamentoAttivita";
import { testoSemplice } from "../utils/testoFormattato";
import { useSottoattivitaDi } from "../utils/sottoattivitaElenco";
import BadgeSottoattivita from "./BadgeSottoattivita";

const COLUMNS = [
  { id:"aperta", label:"📋 Aperta", color:"#2563eb" },
  { id:"in_corso", label:"⚡ In corso", color:"#d97706" },
  { id:"completata", label:"✅ Completata", color:"#059669" },
  { id:"annullata", label:"🚫 Annullata", color:"#9ca3af" },
];

export default function ProjectKanban({ project, onSelectTask, dirittiCtx = null, commessaChiusa = false }) {
  const { currentUser } = useAuth();
  // Spostare una scheda cambia lo stato: solo chi ha pieni diritti sull'attività (utils/condivisione.js)
  const { addToast } = useToast();
  const puoSpostare = (t) => !commessaChiusa && (!dirittiCtx || dirittiPieni(t, dirittiCtx));
  const [tasks, setTasks] = useState([]);
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  useEffect(() => {
    if (!project?.id || !project?.organizationId) return; // commessa aperta solo per id: aspetto i suoi dati
    const q = query(collection(db,"tasks"),where("projectId","==",project.id),where("organizationId","==",project.organizationId));
    return onSnapshot(q, snap=>setTasks(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [project.id, project.organizationId]);

  // Sottoattività: indicatore «⑂ fatte/totali» sulla scheda (stile Asana)
  const sottoPerAttivita = useSottoattivitaDi(tasks.map(t => t.id), project?.organizationId);

  const handleDrop = async (colId) => {
    if (dragging && dragging.status!==colId && puoSpostare(dragging)) {
      avvisaCambioStato(dragging, colId, currentUser);
      const conCompletamento = colId === "completata" || dragging.status === "completata";
      await updateDoc(doc(db,"tasks",dragging.id),{status:colId, ...(conCompletamento ? campiCompletamento(colId, currentUser) : {}), updatedAt:serverTimestamp()});
      const viola = colId === "completata" ? avvisoCompletamentoDiAltri(dragging, currentUser?.uid) : null;
      if (viola) addToast(viola, "viola", 8000);
    }
    setDragging(null); setDragOver(null);
  };

  const today = toLocalDateStr(new Date());

  return (
    <div className="kanban-board">
      {COLUMNS.map(col => {
        const colTasks = tasks.filter(t=>t.status===col.id);
        return (
          <div key={col.id} className="kanban-col"
            style={{border:`2px solid ${dragOver===col.id?col.color:"transparent"}`,transition:"border 0.15s"}}
            onDragOver={e=>{e.preventDefault();setDragOver(col.id);}}
            onDragLeave={()=>setDragOver(null)}
            onDrop={()=>handleDrop(col.id)}>
            <div className="kanban-col-header">
              <span className="kanban-col-title" style={{color:col.color}}>{col.label}</span>
              <span style={{background:col.color+"22",color:col.color,borderRadius:12,padding:"1px 8px",fontSize:12,fontWeight:600}}>{colTasks.length}</span>
            </div>
            {colTasks.map(task => {
              const isOverdue = isTaskOverdue(task);
              return (
                <div key={task.id} className="kanban-card"
                  draggable={puoSpostare(task)} onDragStart={()=>{ if (puoSpostare(task)) setDragging(task); }}
                  onClick={()=>onSelectTask(task)}
                  style={{opacity:dragging?.id===task.id?0.5:1,borderLeft:`3px solid ${task.priority==="alta"?"#dc2626":task.priority==="media"?"#d97706":"#059669"}`}}>
                  <div style={{fontSize:13,fontWeight:500,marginBottom:6,textDecoration:task.status==="completata"?"line-through":"none",color:task.status==="completata"?"#9ca3af":"#111827"}}>{task.title}</div>
                  {task.description && <div style={{fontSize:12,color:"#6b7280",marginBottom:6,overflow:"hidden",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical"}}>{testoSemplice(task.description)}</div>}
                  <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                    <BadgeSottoattivita lista={sottoPerAttivita[task.id]}/>
                    {task.dueDate && <span style={{fontSize:11,color:isOverdue?"#dc2626":"#9ca3af"}}>📅 {task.dueDate.split("-").reverse().join("/")}</span>}
                    {task.assignedName && (
                      <div style={{display:"flex",alignItems:"center",gap:4}}>
                        <div className="avatar" style={{width:18,height:18,fontSize:10}}>{task.assignedName[0]?.toUpperCase()}</div>
                        <span style={{fontSize:11,color:"#6b7280"}}>{task.assignedName}</span>
                      </div>
                    )}
                    <span style={{marginLeft:"auto",fontSize:11,padding:"1px 6px",borderRadius:8,background:task.priority==="alta"?"#fee2e2":task.priority==="media"?"#fef3c7":"#d1fae5",color:task.priority==="alta"?"#dc2626":task.priority==="media"?"#d97706":"#059669"}}>{task.priority}</span>
                  </div>
                </div>
              );
            })}
            {colTasks.length===0 && <div style={{textAlign:"center",padding:"24px 12px",color:"#d1d5db",fontSize:12}}>Trascina qui</div>}
          </div>
        );
      })}
    </div>
  );
}
